import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { RouteChildrenProps } from 'react-router'
import { Forbidden } from 'fejl'
import TextareaAutosize from 'react-textarea-autosize'
import JobRootViewModel from '../JobRoot/JobRootViewModel'
import JobViewModel from '../JobRoot/Job/JobViewModel'
import DocumentsCard from '../JobRoot/Job/DocumentsCard'
import CapturedInformationCard from '../JobRoot/Job/CapturedInformationCard'
import ResponsesCard from '../JobRoot/Job/ResponsesCard'
import AmendmentsCard from '../JobRoot/Job/AmendmentsCard'
import ConsultationCallCard from '../JobRoot/Job/ConsultationCallCard'
import { ChatDisabled, NoConversation } from '../JobRoot/Job/CommunicationView'
import { SectionError, SectionLoading } from '../JobRoot/Job/Section/Section'
import List from 'messaging/components/ConversationView/Messages/List'
import TypingIndicator from 'messaging/components/ConversationView/TypingIndicator'
import ConversationViewModel from 'messaging/components/ConversationView/ConversationViewModel'
import { TabBar, TabItem } from 'components/TabBar'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import AccessDeniedView from 'components/AccessDeniedView'
import ProgressBar from 'components/ProgressBar'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { useDocumentTitle } from 'hooks/document-title'
import { useHideHubspotWidget } from 'tracking/hubspot-widget-visibility'
import styles from './MobileJob.sass'

type MobileTab = 'chat' | 'documents' | 'details' | 'consultation'

interface Props extends RouteChildrenProps<{ jobId: string }> {
  rootVM: JobRootViewModel
}

export default observer(function MobileJob({ rootVM, match }: Props) {
  const id = match?.params?.jobId
  useHideHubspotWidget()

  useEffect(() => {
    rootVM.activate()
    return () => rootVM.deactivate()
  }, [rootVM])

  useEffect(() => {
    rootVM.viewJob(id)
  }, [rootVM, id])

  return (
    <div className={styles.root}>
      {rootVM.viewJob.match({
        pending: () => <ContentLoading />,
        rejected: (err) =>
          err instanceof Forbidden ? (
            <AccessDeniedView title={err.message} />
          ) : (
            <ContentError error={err} />
          ),
        resolved: (vm) => vm && <MobileJobBody vm={vm} />,
      })}
    </div>
  )
})

const MobileJobBody = observer(function MobileJobBody({
  vm,
}: {
  vm: JobViewModel
}) {
  const [tab, setTab] = useState<MobileTab>('chat')
  useDocumentTitle(`${vm.job.workspace.name} | ${vm.job.displayName}`)

  return (
    <>
      <div className={styles.topChrome}>
        <MobileJobHeader vm={vm} />

        <div className={styles.tabScroller}>
          <TabBar bordered align="left" stretch centerText>
            <TabItem
              active={tab === 'chat'}
              onClick={() => setTab('chat')}
              dataTest="mobile-tab-chat"
            >
              Chat
            </TabItem>
            <TabItem
              active={tab === 'documents'}
              onClick={() => setTab('documents')}
              dataTest="mobile-tab-documents"
            >
              Documents
            </TabItem>
            <TabItem
              active={tab === 'details'}
              onClick={() => setTab('details')}
              dataTest="mobile-tab-details"
            >
              Details
            </TabItem>
            {vm.job.consultation && (
              <TabItem
                active={tab === 'consultation'}
                onClick={() => setTab('consultation')}
                dataTest="mobile-tab-consultation"
              >
                Consultation
              </TabItem>
            )}
          </TabBar>
        </div>
      </div>

      {tab === 'chat' ? (
        <MobileJobChat vm={vm} />
      ) : (
        <main className={styles.tabBody}>
          {tab === 'documents' && <DocumentsCard vm={vm} />}
          {tab === 'details' && <MobileDetailsTab vm={vm} />}
          {tab === 'consultation' && vm.job.consultation && (
            <ConsultationCallCard vm={vm} />
          )}
        </main>
      )}
    </>
  )
})

const MobileJobHeader = observer(function MobileJobHeader({
  vm,
}: {
  vm: JobViewModel
}) {
  return (
    <header className={styles.header}>
      <MobileJobProgressionSummary vm={vm} />
    </header>
  )
})

const MobileJobProgressionSummary = observer(
  function MobileJobProgressionSummary({ vm }: { vm: JobViewModel }) {
    const jobProgressionVm = vm.jobProgressionVM
    const jobIsCompleted = vm.job.completed
    const currentStepInfo = jobProgressionVm.currentStepInfo

    return jobProgressionVm.activate.match({
      pending: () => <SectionLoading />,
      rejected: (err) => (
        <SectionError
          title="An error occurred while loading the job progression."
          error={err}
        />
      ),
      resolved: () => (
        <div
          className={styles.progressionSummary}
          data-test="mobile-job-progression-summary"
        >
          <div className={styles.progressionTitle}>
            {jobIsCompleted ? 'Job completed' : currentStepInfo?.title}
          </div>
          <ProgressBar
            bars={[
              {
                progress: jobProgressionVm.completionStatus,
              },
              {
                progress: jobProgressionVm.pendingCompletionStatus,
                style: 'striped',
              },
            ]}
          />
        </div>
      ),
    })
  },
)

const MobileJobChat = observer(function MobileJobChat({
  vm,
}: {
  vm: JobViewModel
}) {
  const currentMember = vm.jobWorkspaceMember
  const canChat = currentMember.hasAllPermissions('CHAT_VIEW')

  if (!canChat) {
    return (
      <div className={styles.chatTab}>
        <ChatDisabled />
      </div>
    )
  }

  if (vm.job.conversation === null) {
    return (
      <div className={styles.chatTab}>
        <NoConversation />
      </div>
    )
  }

  return (
    <MobileConversation
      vm={vm}
      readOnly={
        vm.job.status === 'CLOSED' ||
        !(
          (currentMember.hasPermission('CHAT_ADD') &&
            vm.job.isCurrentMemberInConversation) ||
          currentMember.hasPermission('ADMIN_CONVERSATION_MANAGE') ||
          vm.job.hasOwnerTeamChatPermission
        )
      }
    />
  )
})

const MobileConversation = observer(function MobileConversation({
  vm,
  readOnly,
}: {
  vm: JobViewModel
  readOnly: boolean
}) {
  return (
    <section className={styles.chatTab}>
      <div className={styles.messages}>
        <List vm={vm.conversationVM} />
      </div>
      {!readOnly && (
        <div className={styles.composerArea}>
          <MobileConversationComposer conversationVM={vm.conversationVM} />
          <TypingIndicator vm={vm.conversationVM} />
        </div>
      )}
    </section>
  )
})

const MobileConversationComposer = observer(
  function MobileConversationComposer({
    conversationVM,
  }: {
    conversationVM: ConversationViewModel
  }) {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)

    return (
      <div className={styles.composer}>
        <div
          className={styles.inputContainer}
          onClick={() => textareaRef.current && textareaRef.current.focus()}
        >
          <TextareaAutosize
            className={styles.input}
            ref={textareaRef}
            data-private
            data-test="conversation-input"
            style={{ resize: 'none' }}
            placeholder="Write your message here."
            value={conversationVM.composerText}
            maxRows={8}
            onChange={(e) =>
              conversationVM.setComposerText(
                (e.target as HTMLTextAreaElement).value,
              )
            }
          />
        </div>
        <IconButton
          variant="ghost"
          disabled={!conversationVM.composerText.trim()}
          onClick={() => conversationVM.sendMessage()}
          dataTest="conversation-send-mobile"
        >
          <Icon name="chat-submit" size={32} />
        </IconButton>
      </div>
    )
  },
)

const MobileDetailsTab = observer(function MobileDetailsTab({
  vm,
}: {
  vm: JobViewModel
}) {
  if (vm.showDraftView) {
    return (
      <div className={styles.details}>
        {vm.detailsVM.canViewCapturedInfoCard && (
          <CapturedInformationCard vm={vm} />
        )}
        {vm.canViewAmendments && <AmendmentsCard vm={vm} />}
      </div>
    )
  }

  return (
    <div className={styles.details}>
      {vm.detailsVM.canViewCapturedInfoCard ? (
        <CapturedInformationCard vm={vm} />
      ) : (
        <ResponsesCard vm={vm} />
      )}
    </div>
  )
})
